/**
 * The cost programme as one table that adds up (DESIGN §7): Now, each of the next three years and the three-year total, one row per line. The strip under the expense decision prints it compact; the drawer prints the same table
 * at full size, so the two can never disagree. Without a result every cell prints "–" and the rows keep their place, so the strip holds its height.
 */
import type { ReactNode } from "react";
import { formatMillionsBare } from "../../format";
import { GxTable, type GxColumn, type GxRow } from "../../theme/GxTable";
import { GxNotes } from "../../theme/GxNotes";
import { GROSS_LABEL, NET_LABEL, PROGRAMME_COLUMNS, programmeTable, type CostProgramme, type ProgrammeRow } from "./costProgramme";
import "./programme.css";

const BLANK_ROWS: readonly ProgrammeRow[] = [
  { id: "charge", label: "One-off charge, before tax", cells: [null, null, null, null], total: null },
  { id: "gross", label: GROSS_LABEL, cells: [null, null, null, null], total: null },
  { id: "lost", label: "Fees and deposit margin lost", cells: [null, null, null, null], total: null },
  { id: "net", label: NET_LABEL, cells: [null, null, null, null], total: null },
  { id: "cumulative", label: "Running total after the charge", cells: [null, null, null, null], total: null },
];

const COLUMNS: readonly GxColumn[] = [
  { key: "line", label: "Line" },
  ...PROGRAMME_COLUMNS.map((c, i): GxColumn => ({ key: String(i), label: c, kind: "number" })),
];
/** The programme adds up at a tenth of $1M (see `programmeTable`), so its cells keep that one place; the unit is said once in the note under the table. */
export const PROGRAMME_NOTE = "Millions of dollars, to the tenth: each column and each row adds up as printed.";

export function ProgrammeTable({ programme, testId = "decide.programme.table", compact = false }: { readonly programme: CostProgramme | undefined; readonly testId?: string; readonly compact?: boolean }) {
  const rows = programme === undefined ? BLANK_ROWS : programmeTable(programme);
  const money = (x: number | null): string => (x === null ? "" : formatMillionsBare(x, { signed: true, precision: 1 }));
  const rowTestId = testId === "decide.programme.table" ? "decide.programme" : testId;
  return (
    <>
      <GxTable
        caption="The whole programme, in millions of dollars: each column and each row adds up as printed" columns={COLUMNS} dense={compact} testId={testId}
        rows={rows.map((r, ri): GxRow => {
          const cells: Record<string, ReactNode> = { line: r.label };
          [...r.cells, r.total ?? r.cells.at(-1) ?? 0].forEach((c, i) => { cells[String(i)] = programme === undefined ? "\u2013" : money(c); });
          return { key: r.id, cells, testId: `${rowTestId}.row.${r.id}`, ...(ri === rows.length - 1 ? { kind: "total" as const } : {}) };
        })}
      />
      <GxNotes notes={[PROGRAMME_NOTE]} />
    </>
  );
}
